import { PreviewCard } from "@base-ui/react/preview-card";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";

export type HoverCardProps = PreviewCard.Root.Props;

export interface HoverCardTriggerProps
  extends Omit<PreviewCard.Trigger.Props, "className"> {
  /** Trigger element. Consumers are responsible for passing a focusable element. */
  render: PreviewCard.Trigger.Props["render"];
}

export interface HoverCardContentProps
  extends Pick<
    PreviewCard.Positioner.Props,
    "align" | "alignOffset" | "collisionPadding" | "side" | "sideOffset"
  > {
  /** Fully flexible card contents. */
  children: React.ReactNode;
  /** Optional content class for sizing or layout constraints. */
  className?: string;
}

const hoverCardPositionerClass = "z-[var(--z-overlay-popover,180)]";

const hoverCardPopupClass = cn(
  "rounded-(--radius-sm)",
  "border border-solid border-outline-subtle",
  "bg-surface-container-lowest",
  "text-text-default",
  "shadow-elevation-3",
  "font-(family-name:--font-family-body)",
  "origin-(--transform-origin)",
  "transition-[opacity,transform]",
  "duration-200",
  "ease-in-out",
  "will-change-[opacity,transform]",
  "[&[data-starting-style]]:scale-95",
  "[&[data-starting-style]]:opacity-0",
  "[&[data-ending-style]]:scale-95",
  "[&[data-ending-style]]:opacity-0",
);

function HoverCard({ children, ...props }: HoverCardProps) {
  return <PreviewCard.Root {...props}>{children}</PreviewCard.Root>;
}

function HoverCardTrigger({
  render,
  delay = 0,
  ...props
}: HoverCardTriggerProps) {
  return <PreviewCard.Trigger render={render} delay={delay} {...props} />;
}

function HoverCardContent({
  children,
  className,
  side = "bottom",
  align = "center",
  sideOffset = 8,
  ...positionerProps
}: HoverCardContentProps) {
  const portalHost = usePortalHost();

  return (
    <PreviewCard.Portal container={portalHost}>
      <PreviewCard.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={hoverCardPositionerClass}
        {...positionerProps}
      >
        <PreviewCard.Popup className={cn(hoverCardPopupClass, className)}>
          {children}
        </PreviewCard.Popup>
      </PreviewCard.Positioner>
    </PreviewCard.Portal>
  );
}

export { HoverCard, HoverCardTrigger, HoverCardContent };
